import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QueryTag, SearchParam, TableType } from '@common/ts/enums';
import { Loader2 } from 'lucide-react';
import { useQuery, InvalidateQueryFilters, useQueryClient } from '@tanstack/react-query';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import PaperAirplaneIcon from '@/components/icons/PaperAirplaneIcon';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs';
import { EProjectStatus, EStatusCode, NavigationTabs, TranscriptsView } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useMutateReRunAnalyze } from '@/reactQuery/post';
import useErrorStore from '@/store/ErrorStore';
import useGlobalProgressStore from '@/store/GlobalProgressStore';
import useReportStore from '@/store/ReportStore';
import useResultTabStore from '@/store/ResultsTabStore';
import AISearch from '../Content/AISearch';
import ResearchAssistant from '../Content/ResearchAssistant';
import ConversationsTranscripts from '../Content/ConversationsTranscripts';
import Overview from '../Content/Overview';
import Questions from '../Content/Questions';
import ProjectReport from './ProjectReport';
import ProjectTabs from '@/components/project-tabs/ProjectTabs';
import FiltersSelect from '@/components/projects/single-project/filters/FiltersSelect';
import CompareTab from '@/components/projects/single-project/comparison/CompareTab';
import { useGetTranslationLanguages } from '@/reactQuery/get';
import DownloadAnalysisButton from '@/components/projects/single-project/DownloadAnalysisButton';
import CoLoopSendButton from '@/components/integrations/CoLoopSendButton';
import TranscriptPanel from '@/components/transcript-panel/TranscriptPanel';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useStaleFilterRecovery } from '@/hooks/useStaleFilterRecovery';
import { API_ENDPOINTS } from '@/api-endpoints';
import { useUrlTable } from '@/hooks/useUrlTable';
import { RunStateResponse, MultiViewUrlTableProps } from '@/types';
import { getPaginationParamPage, getSortingParamBy, getSortingParamType } from '@/utils/urlParams';

const REFETCH_INTERVAL = 10000;

function ProjectResults() {
  const { lang } = useTranslation();
  const { activeTab, setActiveTab } = useResultTabStore();
  const { project_id: projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const queryClient = useQueryClient();
  // TELV1-606 — a filter_id whose filter no longer exists is dropped page-wide.
  useStaleFilterRecovery(projectId);
  const { clearReportData } = useReportStore();
  const { currentTheme } = useTheme();
  const { setShowError } = useErrorStore();
  const [rerunCacheState, setRerunCacheState] = useState(
    () => localStorage.getItem(`rerun_state_${projectId}`) || '',
  );
  const errorHandledRef = useRef(false);
  // Seed from the URL so a refresh / deep-link with ?question_id renders the
  // right question from first paint; Questions.tsx confirms it once the
  // question list resolves. TELV1-322.
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>(
    () => searchParams.get(SearchParam.QUESTION_ID) ?? '',
  );

  // Measure the sticky tab bar via ResizeObserver so TabsContent can pad
  // dynamically instead of guessing with hardcoded breakpoint values.
  // We use offsetHeight (border box) not contentRect.height (content box)
  // because the tab bar has py-2 padding that's part of its visual height.
  // contentRect excludes padding, causing a ~17px undercount that makes the
  // RA container overlap the tab bar.
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [tabBarHeight, setTabBarHeight] = useState(61);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      setTabBarHeight(el.offsetHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tabContentPaddingStyle = { paddingTop: tabBarHeight + 16 };

  const [isLoadingBeforeApplyFilter, setIsLoadingBeforeApplyFilter] = useState(false);
  const [rerunLoadingState, setRerunLoadingState] = useState<'idle' | 'loading' | 'done' | 'error'>(
    // If there's an active run state in localStorage, assume loading until first poll confirms
    () => (localStorage.getItem(`rerun_state_${projectId}`) ? 'loading' : 'idle'),
  );
  const rerunChacheStateLocalStorageFilter = localStorage.getItem(
    `rerun_state_${projectId}_filter_id`,
  );
  const [showTranscriptsLoader, setShowTranscriptsLoader] = useState(false);
  const [enableFetch, setEnableFetch] = useState(true);
  const currentFilterId = localStorage.getItem(`rerun_filter_${filterId}`);
  const isRerunLoading = rerunLoadingState === 'loading';
  const { mutateAsync: mutateAsyncReRun, isPending: isPendingReRun } = useMutateReRunAnalyze();
  const hasIncompleteTranscriptsPagination = !!searchParams.get(
    getPaginationParamPage(TableType.INCOMPLETE_TRANSCRIPTS),
  );
  const hasCompletedTranscriptsPagination = !!searchParams.get(
    getPaginationParamPage(TableType.TRANSCRIPTS),
  );
  const hasScreenedOutTranscriptsPagination = !!searchParams.get(
    getPaginationParamPage(TableType.SCREENED_OUT_TRANSCRIPTS),
  );
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const transcriptsViewParam = searchParams.get(SearchParam.TRANSCRIPTS_VIEW);

  // Determine the active view from URL parameter
  const getActiveViewFromUrl = (): TranscriptsView => {
    if (translationLanguage) return TranscriptsView.COMPLETED;

    switch (transcriptsViewParam) {
      case TranscriptsView.INCOMPLETE:
        return TranscriptsView.INCOMPLETE;
      case TranscriptsView.SCREENED_OUT:
        return TranscriptsView.SCREENED_OUT;
      case TranscriptsView.COMPLETED:
        return TranscriptsView.COMPLETED;
      default:
        return TranscriptsView.COMPLETED;
    }
  };

  const [activeView, setActiveView] = useState<TranscriptsView>(getActiveViewFromUrl);

  const isCompletedTranscriptsView = activeView === TranscriptsView.COMPLETED;

  const { data: languages } = useGetTranslationLanguages(projectId);

  // Determine table type based on translation mode and active view
  const getTableType = () => {
    if (translationLanguage) return TableType.TRANSLATED_TRANSCRIPTS;
    if (activeView === TranscriptsView.SCREENED_OUT) return TableType.SCREENED_OUT_TRANSCRIPTS;
    if (activeView === TranscriptsView.INCOMPLETE) return TableType.INCOMPLETE_TRANSCRIPTS;
    return TableType.TRANSCRIPTS;
  };

  const {
    urlCurrentPage,
    updateUrlPage,
    urlSorting,
    updateUrlSorting,
    updateUrlFilter,
    getUrlFilterValue,
  } = useUrlTable({ type: getTableType(), activeView }) as MultiViewUrlTableProps;

  // Only treat filters as "processing" if a re-run is actually in progress.
  // Stale localStorage entries from crashed/interrupted runs should not permanently block filters.
  const rerunFilterValues = isRerunLoading
    ? Object.keys(localStorage)
        .filter((key: string) => key.startsWith('rerun_filter_'))
        .map((key) => localStorage.getItem(key))
        .filter((value): value is string => value !== null)
    : [];
  const rerunFilter = localStorage.getItem(`rerun_filter_${filterId}`);
  const addQueryParams = useCallback(
    (params: Record<string, string>) => {
      const newSearchParams = new URLSearchParams(searchParams);
      Object.entries(params).forEach(([key, value]) => {
        newSearchParams.set(key, value);
      });
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const { currentProject: singleProjectData, error: projectDataError } = useCurrentProject();

  const removeQueryParams = (keys: string[]) => {
    keys.forEach((key) => {
      searchParams.delete(key);
    });
    setSearchParams(searchParams);
  };

  const { data: newConversationsCount, refetch } = useQuery<RunStateResponse>({
    queryFn: () =>
      axiosInstanceAnalyze.get(
        `${API_ENDPOINTS.RUN.GET_RUN_STATE(projectId)}/${filterId ? `?filter_id=${filterId}` : ''}`,
      ),
    queryKey: QUERY_KEYS.NEW_CONVERSATIONS_COUNT(projectId, filterId),
    select: (data) => data?.data,
    enabled: rerunCacheState !== null && enableFetch && activeView !== TranscriptsView.INCOMPLETE,
    refetchInterval: REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
  });

  // TELV1-312 — `force` threaded through so callers can bypass the
  // analyzer's "no new conversations" short-circuit. The stale-schema
  // banners (RA + Compare) need this on COMPLETED projects: every
  // conversation is already DIGESTED, so without force=true the runner
  // returns DONE immediately, the toast claims success, but no pipeline
  // runs and the rich schema is never written. Filter changes still
  // omit force (the default false path) — they only need to re-run when
  // there's genuinely new data to crunch.
  const onReRun = useCallback(
    async (f_id?: string | null, force?: boolean): Promise<void> => {
      try {
        errorHandledRef.current = false; // Reset error guard for new run
        if (f_id) {
          setIsLoadingBeforeApplyFilter(true);
        }
        const res = await mutateAsyncReRun({ project_id: projectId, f_id, force });
        setRerunLoadingState(res?.data?.state);
        setRerunCacheState(res?.data?.state_id);
        localStorage.setItem(`rerun_state_${projectId}`, res?.data?.state_id);
        if (f_id) {
          localStorage.setItem(`rerun_state_${projectId}_filter_id`, f_id);
        }
      } catch {
        setIsLoadingBeforeApplyFilter(false);
        toast.error(lang.get('msg.errorPleaseTryAgain'));
      }
    },
    [mutateAsyncReRun, projectId, lang],
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', value);
      setSearchParams(newParams);
      scrollToTop();
    },
    [scrollToTop, searchParams, setSearchParams],
  );

  const analysisLanguage = singleProjectData?.analysis_language;

  // Filters, Compare, and Research Assistant are gated to COMPLETED
  // projects only. On Live/Published projects the underlying analysis
  // is still being generated as new responses come in, so filter
  // segments, comparisons, and the RA agentic loop would all run on
  // data that's moving under them. Mark the project complete when
  // data collection is done, then these tools unlock.
  const showFilters = singleProjectData?.status === EProjectStatus.COMPLETED;
  // Download Analysis is a read-only export that's useful as soon as
  // there's any analysis to download, so it remains available on both
  // Live and Completed projects (kept from the previous behaviour).
  const showDownload =
    singleProjectData?.status === EProjectStatus.COMPLETED ||
    singleProjectData?.status === EProjectStatus.PUBLISHED;

  // Restore active tab from URL on mount, default to Overview
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearReportData();
    const tabFromUrl = searchParams.get('tab');
    const validTabs = Object.values(NavigationTabs) as string[];
    setActiveTab(
      tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : NavigationTabs.OVERVIEW,
    );
  }, [clearReportData, setActiveTab]);

  // Safety rail: if the user is sitting on a COMPLETED-only tab
  // (Compare or Research Assistant) and the project is not in
  // COMPLETED state, bounce them to Overview. This covers:
  //   - deep links to #compare or #ai_search on a Live project
  //   - admin users who changed the project back to PUBLISHED while
  //     a researcher was already inside Compare/RA in another tab
  //   - any race where the tab was mounted before singleProjectData
  //     resolved and the initial tab selection ran against an
  //     optimistic COMPLETED state
  // The ProjectTabs component already hides those tab triggers on
  // non-COMPLETED projects, so this effect only fires if the state
  // was loaded AFTER the tab was already selected.
  useEffect(() => {
    if (!singleProjectData?.status) return;
    const isCompletedOnlyTab =
      activeTab === NavigationTabs.COMPARE || activeTab === NavigationTabs.AISEARCH;
    if (isCompletedOnlyTab && singleProjectData.status !== EProjectStatus.COMPLETED) {
      setActiveTab(NavigationTabs.OVERVIEW);
    }
  }, [activeTab, singleProjectData?.status, setActiveTab]);

  // TELV1-330: deep-link to Transcripts ONLY on initial mount, when the URL
  // carries a transcripts-pagination param and doesn't explicitly select a
  // different tab. The previous version kept `activeTab` in its deps, so it
  // re-fired on EVERY tab switch and re-forced Transcripts while a stale
  // `*_page` param lingered in the URL — so clicking Compare (or any other
  // non-Questions tab) did nothing until a navigate-away cleared the param.
  // Running once on mount (and honouring an explicit `?tab=`) keeps it a
  // genuine deep-link that never overrides a later user tab click.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const explicitlySelectedOtherTab = !!tabFromUrl && tabFromUrl !== NavigationTabs.TRANSCRIPTS;
    if (
      !explicitlySelectedOtherTab &&
      (hasCompletedTranscriptsPagination ||
        hasIncompleteTranscriptsPagination ||
        hasScreenedOutTranscriptsPagination)
    ) {
      setActiveTab(NavigationTabs.TRANSCRIPTS);
    }
  }, []);

  // Sync activeView with URL parameters when they change (e.g., browser back/forward)
  useEffect(() => {
    const urlView = getActiveViewFromUrl();
    if (urlView !== activeView) {
      setActiveView(urlView);
    }
  }, [
    transcriptsViewParam,
    hasIncompleteTranscriptsPagination,
    hasCompletedTranscriptsPagination,
    hasScreenedOutTranscriptsPagination,
    translationLanguage,
    activeView,
  ]);

  useEffect(() => {
    setRerunLoadingState(newConversationsCount?.state);
    if (newConversationsCount?.state) {
      setIsLoadingBeforeApplyFilter(false);
    }
  }, [newConversationsCount]);

  useEffect(() => {
    if (rerunLoadingState === 'done') {
      setIsLoadingBeforeApplyFilter(false);

      const handleReRunComplete = async () => {
        try {
          if (!filterId || (filterId && currentFilterId)) {
            await queryClient.invalidateQueries(
              QUERY_KEYS.INTERVIEW_OVERVIEW(projectId, filterId) as InvalidateQueryFilters,
            );
            await queryClient.invalidateQueries(
              QUERY_KEYS.INTERVIEW_QUESTION_INSIGHTS(
                projectId,
                selectedQuestionId,
                filterId,
              ) as InvalidateQueryFilters,
            );
            await queryClient.invalidateQueries([
              QueryTag.QUESTION_TRANSCRIPTS,
            ] as InvalidateQueryFilters);
            await queryClient.invalidateQueries(
              QUERY_KEYS.INTERVIEW_QUESTION_CATEGORIES(
                projectId,
                selectedQuestionId,
                filterId,
              ) as InvalidateQueryFilters,
            );
            await queryClient.invalidateQueries(
              QUERY_KEYS.NEW_CONVERSATIONS_COUNT(projectId, filterId) as InvalidateQueryFilters,
            );
          }

          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('rerun_filter_')) {
              localStorage.removeItem(key);
            }
          });
          localStorage.removeItem(`rerun_state_${projectId}`);
          localStorage.removeItem(`rerun_state_${projectId}_filter_id`);

          if (singleProjectData?.status !== EProjectStatus.COMPLETED) {
            toast.success('Re-run is done!');
          } else {
            if (rerunChacheStateLocalStorageFilter) {
              addQueryParams({ filter_id: rerunChacheStateLocalStorageFilter });
            }
            removeQueryParams(['edit']);
            if (!filterId) {
              toast.success('Analysis updated successfully.');
              await queryClient.invalidateQueries([
                QueryTag.INTERVIEW_QUESTION_CATEGORIES,
              ] as InvalidateQueryFilters);
              return;
            }
            if (currentFilterId) {
              toast.success(lang.get('msg.filterAppliedSuccessfully'));
              await queryClient.invalidateQueries([
                QueryTag.INTERVIEW_QUESTION_CATEGORIES,
              ] as InvalidateQueryFilters);
            }
          }
        } catch (error) {
          console.error('Error during re-run completion handling:', error);
          setIsLoadingBeforeApplyFilter(false);
        }
      };

      handleReRunComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunLoadingState, projectId, singleProjectData?.status, rerunChacheStateLocalStorageFilter]);

  useEffect(() => {
    if (rerunLoadingState === 'error' && !errorHandledRef.current) {
      // Guard prevents infinite toast loop: Redis ERROR state persists for 6 hours,
      // and polling re-fires every 10s. Without this guard, toast would fire repeatedly.
      errorHandledRef.current = true;
      setIsLoadingBeforeApplyFilter(false);
      toast.error('Analysis failed. Please try again.');

      // Clean up localStorage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('rerun_filter_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem(`rerun_state_${projectId}`);
      localStorage.removeItem(`rerun_state_${projectId}_filter_id`);

      // Reset state to allow retry
      setRerunLoadingState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunLoadingState, projectId]);

  // Publish re-run state to global progress bar.
  // The bar auto-hides on this page because the owning project matches
  // the current Results view (see GlobalProgressBar filter). It becomes
  // visible when the user navigates to Design/Share/dashboard/elsewhere
  // so they don't lose sight of the running analysis.
  useEffect(() => {
    const progressId = `analysis-rerun-${projectId}`;
    if (rerunLoadingState === 'loading' && projectId) {
      useGlobalProgressStore.getState().addEntry({
        id: progressId,
        type: 'analysis',
        label: 'Analysis running...',
        startedAt: Date.now(),
        projectId,
        returnUrl: `${window.location.pathname}${window.location.search}`,
      });
    } else {
      useGlobalProgressStore.getState().removeEntry(progressId);
    }
    return () => {
      useGlobalProgressStore.getState().removeEntry(progressId);
    };
  }, [rerunLoadingState, projectId]);

  useEffect(() => {
    if ((projectDataError as AxiosError)?.status === EStatusCode.BAD_REQUEST) {
      setShowError(true);
    }
  }, [projectDataError, setShowError]);

  const hasDeletedConversations = newConversationsCount?.hasDeletionsSinceLastRun;
  const isRerunDisabled =
    !isPendingReRun &&
    newConversationsCount?.numberOfUnRunConversations === 0 &&
    !hasDeletedConversations;

  const reRunButtonText = () => {
    if (isRerunLoading) return 'Re-running';

    if (isRerunDisabled) return 'No new conversations';

    if (hasDeletedConversations) {
      return 'Re-run (deletions)';
    }

    return 'Re-run';
  };

  // Add a 300ms loader for Transcripts tab
  const setActiveViewHandler = (view: TranscriptsView) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(SearchParam.TRANSCRIPTS_VIEW, view);
    // Clear sort state — views may have different sortable columns
    newSearchParams.delete(getSortingParamBy(getTableType()));
    newSearchParams.delete(getSortingParamType(getTableType()));
    setSearchParams(newSearchParams);
    setShowTranscriptsLoader(true);
    setTimeout(() => {
      setActiveView(view);
      setShowTranscriptsLoader(false);
    }, 300);
  };

  return (
    <main
      style={{ backgroundColor: currentTheme?.background, color: currentTheme?.['primary-text'] }}
      className="flex flex-col w-full h-full"
    >
      {/* tabs */}
      <Tabs
        defaultValue="overview_tab"
        className="w-full"
        value={activeTab}
        onValueChange={handleTabChange}
      >
        <div
          ref={tabBarRef}
          style={{
            backgroundColor: currentTheme?.background,
            color: currentTheme?.['primary-text'],
          }}
          className="fixed left-0 right-0 flex flex-wrap items-center z-[99] py-2 lg:py-2 -mt-[9px] border-b px-[2%]"
        >
          <TabsList
            style={{
              backgroundColor: currentTheme?.background,
              color: currentTheme?.['primary-text'],
            }}
            className="relative flex items-center justify-start h-fit gap-1 lg:gap-0"
          >
            <ProjectTabs
              currentTheme={currentTheme}
              lang={lang}
              activeTab={activeTab}
              projectStatus={singleProjectData?.status}
            />
          </TabsList>

          {/* Right-aligned controls: filters + download + re-run */}
          <div className="flex items-center gap-3 shrink-0 ml-auto">
            {showFilters && activeTab !== NavigationTabs.TRANSCRIPTS && (
              <FiltersSelect
                onReRun={onReRun}
                rerunFilterValues={rerunFilterValues}
                onFilterDeleteEnableRefetch={setEnableFetch}
                refetch={refetch}
                onCompare={() => setActiveTab(NavigationTabs.COMPARE)}
              />
            )}

            {showDownload &&
              (activeTab === NavigationTabs.OVERVIEW || activeTab === NavigationTabs.QUESTIONS) && (
                <>
                  <DownloadAnalysisButton disabled={isPendingReRun || isRerunLoading} />
                  <CoLoopSendButton disabled={isPendingReRun || isRerunLoading} />
                </>
              )}

            {singleProjectData?.status !== EProjectStatus.COMPLETED && (
              <Button
                variant="default"
                disabled={isRerunDisabled || isRerunLoading}
                className="relative flex items-center gap-2 font-bold px-5"
                onClick={() => onReRun(filterId)}
              >
                {isPendingReRun || isRerunLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="size-4" />
                )}
                {reRunButtonText()}
                {hasDeletedConversations && (
                  <span className="absolute flex items-center justify-center h-3 w-3 bg-amber-200 rounded-full -top-1 -right-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  </span>
                )}
                {rerunLoadingState !== 'loading' &&
                  newConversationsCount?.numberOfUnRunConversations > 0 &&
                  !hasDeletedConversations && (
                    <span className="absolute flex items-center justify-center h-5 px-1.5 text-xs text-white bg-red-600 rounded-full w-fit -top-1 -right-1.5">
                      {newConversationsCount?.numberOfUnRunConversations}
                    </span>
                  )}
              </Button>
            )}

            {/* Progress indicator — shows analysis progress inline.
                Two modes based on project status:

                COMPLETED: The Re-run button is hidden, so this is the
                ONLY loading feedback. Always render when isRerunLoading,
                including a spinner icon and a fallback "Running..." text
                for the brief window before per-question progress starts.

                Non-COMPLETED (LIVE / PUBLISHED): The Re-run button
                already shows a spinner + "Re-running", so only render
                the secondary text when it adds NEW information — i.e.
                per-question progress ("Analyzing question 5/12...") or
                a filter-scoped message ("Applying filter..."). Without
                this guard the user would see the button "Re-running"
                and a redundant "Running..." label side by side. */}
            {isRerunLoading &&
              singleProjectData?.status === EProjectStatus.COMPLETED &&
              (() => {
                // Resolve the secondary-status label via early returns instead
                // of a nested ternary expression (eslint: no-nested-ternary).
                // Ordering matters: per-question progress wins over the
                // filter / generic fallback because it carries the most
                // specific information available.
                const progress = newConversationsCount?.progress;
                let label: string;
                if (progress) {
                  if (progress.step === 'overall') {
                    label = 'Generating overview...';
                  } else if (progress.step === 'batches') {
                    label = `Processing batch ${progress.completed}/${progress.total}...`;
                  } else if (progress.step === 'synthesis') {
                    label = `Synthesizing question ${progress.completed}/${progress.total}...`;
                  } else {
                    label = `Analyzing question ${progress.completed}/${progress.total}...`;
                  }
                } else if (filterId) {
                  label = lang.get('msg.applyingFilter');
                } else {
                  label = lang.get('msg.running');
                }
                return (
                  <span className="flex items-center gap-2 text-sm text-neutral-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {label}
                  </span>
                );
              })()}
          </div>
        </div>

        <TabsContent
          value={NavigationTabs.OVERVIEW}
          style={tabContentPaddingStyle}
          className={cn('pb-10 !ring-transparent !ring-0')}
        >
          {activeTab === NavigationTabs.OVERVIEW && (
            <Overview
              rerunFilter={rerunFilter}
              isPendingReRun={isPendingReRun}
              analysisLanguage={analysisLanguage}
              newConversationsCount={newConversationsCount?.numberOfUnRunConversations}
              newConversationsState={newConversationsCount?.state}
              isRerunLoading={isRerunLoading}
              onReRun={onReRun}
              isLoadingBeforeApplyFilter={isLoadingBeforeApplyFilter}
              analysisProgress={newConversationsCount?.progress}
              outcome={newConversationsCount?.outcome}
            />
          )}
        </TabsContent>
        <TabsContent
          value={NavigationTabs.TRANSCRIPTS}
          style={tabContentPaddingStyle}
          className={cn('!ring-transparent !ring-0')}
        >
          {activeTab === NavigationTabs.TRANSCRIPTS && (
            <ConversationsTranscripts
              languages={languages}
              // Props for filters and view toggle
              isLoading={showTranscriptsLoader}
              showFilters={showFilters}
              onReRun={onReRun}
              rerunFilterValues={rerunFilterValues}
              onFilterDeleteEnableRefetch={setEnableFetch}
              refetch={refetch}
              activeView={activeView}
              onViewChange={setActiveViewHandler}
              showIncompleteToggle={!translationLanguage}
              showScreenedOutToggle={!translationLanguage}
              projectStatus={singleProjectData?.status}
              deletedConversations={singleProjectData?.deleted_conversations}
              isCompletedTranscriptsView={isCompletedTranscriptsView}
              // URL table props for pagination, sorting, filtering
              urlCurrentPage={urlCurrentPage}
              updateUrlPage={updateUrlPage}
              urlSorting={urlSorting}
              updateUrlSorting={updateUrlSorting}
              updateUrlFilter={updateUrlFilter}
              getUrlFilterValue={getUrlFilterValue}
              tableType={getTableType()}
            />
          )}
        </TabsContent>
        <TabsContent
          value={NavigationTabs.QUESTIONS}
          style={tabContentPaddingStyle}
          className={cn('pb-10 !ring-transparent !ring-0')}
        >
          {activeTab === NavigationTabs.QUESTIONS && (
            <Questions
              analysisLanguage={analysisLanguage}
              isPendingReRun={isPendingReRun}
              newConversationsCount={newConversationsCount?.numberOfUnRunConversations}
              newConversationsState={newConversationsCount?.state}
              isRerunLoading={isRerunLoading}
              onReRun={onReRun}
              isLoadingBeforeApplyFilter={isLoadingBeforeApplyFilter}
              setSelectedQuestionId={setSelectedQuestionId}
              selectedQuestionId={selectedQuestionId}
            />
          )}
        </TabsContent>
        {/* RA stays mounted (CSS-hidden) so SSE streams survive tab
            switches. forceMount keeps the Radix TabsContent in the DOM
            when inactive. We add our own `hidden` class when not active
            because forceMount disables Radix's automatic hiding. The
            `inert` attribute prevents keyboard/screen-reader interaction
            when hidden. Other tabs remain conditionally rendered — only
            RA needs background persistence. */}
        {/* `inert` (used in the spread below) is a valid HTML attribute
            (Chromium 102+) but the React typings haven't caught up
            project-wide. Cast through `unknown` to satisfy strict eslint
            without `any`; the runtime payload is a bare attribute
            presence + empty string value, which the browser treats as
            "inert subtree". */}
        <TabsContent
          value={NavigationTabs.AISEARCH}
          style={activeTab === NavigationTabs.AISEARCH ? tabContentPaddingStyle : undefined}
          className={cn(
            '!pb-0 overflow-hidden !ring-transparent !ring-0',
            activeTab !== NavigationTabs.AISEARCH && 'hidden',
          )}
          forceMount
          {...(activeTab !== NavigationTabs.AISEARCH
            ? ({ inert: '' } as unknown as Record<string, string>)
            : {})}
        >
          <ResearchAssistant
            isRerunLoading={isRerunLoading}
            tabBarHeight={tabBarHeight}
            onReRun={onReRun}
          />
        </TabsContent>
        <TabsContent
          value={NavigationTabs.COMPARE}
          style={tabContentPaddingStyle}
          className={cn('pb-10 !ring-transparent !ring-0')}
        >
          {activeTab === NavigationTabs.COMPARE && (
            <CompareTab
              analysisLanguage={analysisLanguage}
              onReRun={onReRun}
              isRerunLoading={isRerunLoading}
            />
          )}
        </TabsContent>
        <TabsContent
          value={NavigationTabs.REPORTS}
          style={tabContentPaddingStyle}
          className={cn('pb-10 !ring-transparent !ring-0')}
        >
          {activeTab === NavigationTabs.REPORTS && (
            <ProjectReport isPendingReRun={isPendingReRun} analysisLanguage={analysisLanguage} />
          )}
        </TabsContent>
      </Tabs>

      <TranscriptPanel />
    </main>
  );
}

export default ProjectResults;
